/**
 * Onboarding Tab Component
 *
 * Displays onboarding tasks and progress for recruits.
 * This component will manage the onboarding workflow from approval through completion.
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LwModal } from '@/components/lw';
import { CheckCircle2, Circle, Plus, Mail, Lock, AlertCircle, User, Loader2, ExternalLink, Info, CheckCircle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/utils/supabase';
import type { OnboardingTask } from './onboarding-types';
import { IDDocumentsChecklist } from './onboarding/IDDocumentsChecklist';
import { Checkbox } from '@/components/ui/checkbox';
import { createGmailAccount, createUserFromRecruit, getLinkableUsers, linkRecruitToUser, AVAILABLE_ROLES, type WorkspaceErrorCode } from '@/services/workspaceService';
import { recruiterNotificationService } from '@/services/recruiterNotificationService';
import { RecruitmentInvitationService } from '@/services/recruitmentInvitationService';
import { createComponentLogger } from '@/utils/logger';
import { parseAsLocalDate } from '@/utils/dateHelpers';

const log = createComponentLogger('OnboardingTab', 'recruitment');

interface OnboardingTabProps {
  candidateId?: string;
  tasks: OnboardingTask[];
  onTasksChange: (tasks: OnboardingTask[]) => void;
  onEmailDeliveryChange?: () => void | Promise<void>;
}

export const OnboardingTab: React.FC<OnboardingTabProps> = ({
  candidateId,
  tasks,
  onTasksChange,
  onEmailDeliveryChange
}) => {
  const [isCreatingEncompass, setIsCreatingEncompass] = useState(false);
  const [linkedUserId, setLinkedUserId] = useState<string | null>(null);
  const [isRequestingXactus, setIsRequestingXactus] = useState(false);
  const [xactusModalOpen, setXactusModalOpen] = useState(false);
  const [xactusIframeUrl, setXactusIframeUrl] = useState<string>('');
  const [nmlsAuthDate, setNmlsAuthDate] = useState<string>('');
  const [isSavingNmls, setIsSavingNmls] = useState(false);
  const [nmlsSavedDate, setNmlsSavedDate] = useState<string | null>(null);
  const [isCreatingGmail, setIsCreatingGmail] = useState(false);
  const [gmailCredentials, setGmailCredentials] = useState<{ email: string; tempPassword: string } | null>(null);
  const [gmailError, setGmailError] = useState<{ message: string; code: WorkspaceErrorCode } | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['loan_officer']); // Default to loan officer
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [recruitGmailAccount, setRecruitGmailAccount] = useState<string | null>(null);

  // Link Existing Gmail state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkableUsers, setLinkableUsers] = useState<Array<{ id: string; email: string; fullName: string }>>([]);
  const [loadingLinkableUsers, setLoadingLinkableUsers] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [selectedLinkUserId, setSelectedLinkUserId] = useState<string | null>(null);
  const [isLinkingExisting, setIsLinkingExisting] = useState(false);
  const [linkedExistingEmail, setLinkedExistingEmail] = useState<string | null>(null);

  // Encompass organization and persona state
  const [encompassOrganizations, setEncompassOrganizations] = useState<Array<{id: string; name: string; path: string}>>([]);
  const [encompassPersonas, setEncompassPersonas] = useState<Array<{id: string; name: string}>>([]);
  const [selectedEncompassOrgId, setSelectedEncompassOrgId] = useState<string>('');
  const [selectedEncompassPersonaId, setSelectedEncompassPersonaId] = useState<string>('7'); // Default: Loan Officer
  const [loadingEncompassSettings, setLoadingEncompassSettings] = useState(false);

  const encompassSyncUrl = `${supabase.supabaseUrl}/functions/v1/encompass-sync`;

  // Calculate progress
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const totalTasks = tasks.length;
  const progressPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // Handle field value changes
  const handleFieldChange = (taskId: string, fieldId: string, value: string) => {
    onTasksChange(
      tasks.map(task =>
        task.id === taskId
          ? {
              ...task,
              fieldValues: {
                ...task.fieldValues,
                [fieldId]: value
              }
            }
          : task
      )
    );
  };

  // Validate email domain
  const validateEmail = (email: string): boolean => {
    const pattern = /^[a-zA-Z0-9._%+-]+@lendwisemtg\.com$/;
    return pattern.test(email);
  };

  // Initialize NMLS date from task data
  useEffect(() => {
    const task5 = tasks.find(t => t.id === '5');
    if (task5?.fieldValues?.nmls_authorization_date) {
      setNmlsAuthDate(task5.fieldValues.nmls_authorization_date);
      setNmlsSavedDate(task5.fieldValues.nmls_authorization_date);
    }
  }, [tasks]);

  // Initialize linkedUserId from Task 2 if already completed (from previous session)
  useEffect(() => {
    const task2 = tasks.find(t => t.id === '2');
    if (task2?.status === 'completed' && task2.fieldValues?.linked_user_id && !linkedUserId) {
      setLinkedUserId(task2.fieldValues.linked_user_id);
    }
  }, [tasks, linkedUserId]);

  // Fetch recruit's Gmail account for Task 2
  useEffect(() => {
    const fetchRecruitGmail = async () => {
      if (!candidateId) {
return;
}

      const { data: recruit } = await supabase
        .from('recruits')
        .select('gmail_account')
        .eq('id', candidateId)
        .single();

      if (recruit?.gmail_account) {
        setRecruitGmailAccount(recruit.gmail_account);
      }
    };

    fetchRecruitGmail();
  }, [candidateId]);

  // Load Encompass settings when user is linked and Task 4 not completed
  useEffect(() => {
    const task4 = tasks.find(t => t.id === '4');
    const isTask4Completed = task4?.status === 'completed';

    if (linkedUserId && !isTask4Completed && encompassOrganizations.length === 0) {
      loadEncompassSettings();
    }
    // Runs on link/task change; loadEncompassSettings + org-length are intentionally omitted (fetch-once guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedUserId, tasks]);

  // Handle creating user from recruit (new Task 2 flow)
  const handleCreateUserFromRecruit = async () => {
    if (!candidateId) {
      toast.error('Candidate ID not found');
      return;
    }

    if (selectedRoles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }

    setIsCreatingUser(true);

    try {
      const result = await createUserFromRecruit(candidateId, selectedRoles);

      if (!result.success) {
        toast.error('Failed to create user account', {
          description: result.error || 'Unknown error'
        });
        return;
      }

      // Store the linked user ID for subsequent tasks
      setLinkedUserId(result.userId || null);

      // Mark Task 2 as completed
      onTasksChange(
        tasks.map(t =>
          t.id === '2'
            ? {
                ...t,
                status: 'completed',
                completedAt: new Date().toISOString(),
                fieldValues: {
                  linked_user_id: result.userId || '',
                  linked_user_email: result.email || '',
                  roles: result.roles?.join(', ') || ''
                }
              }
            : t
        )
      );

      toast.success('User account created and linked', {
        description: `${result.email} can now log in with roles: ${result.roles?.join(', ')}`
      });
    } catch (error) {
      console.error('Error creating user from recruit:', error);
      toast.error('Failed to create user account', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Handle role selection toggle
  const handleRoleToggle = (roleValue: string, checked: boolean) => {
    if (checked) {
      setSelectedRoles(prev => [...prev, roleValue]);
    } else {
      setSelectedRoles(prev => prev.filter(r => r !== roleValue));
    }
  };

  // Load Encompass organization and persona settings
  const loadEncompassSettings = async () => {
    if (!linkedUserId) {
return;
}

    setLoadingEncompassSettings(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
return;
}

      const response = await fetch(
        encompassSyncUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`
          },
          body: JSON.stringify({
            action: 'get-settings',
            userId: linkedUserId
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setEncompassOrganizations(result.organizations || []);
        setEncompassPersonas(result.personas || []);

        // Set defaults
        if (result.defaults?.organizationId) {
          setSelectedEncompassOrgId(result.defaults.organizationId);
        }
        if (result.defaults?.personaId) {
          setSelectedEncompassPersonaId(result.defaults.personaId);
        }
      }
    } catch (error) {
      console.error('Failed to load Encompass settings:', error);
    } finally {
      setLoadingEncompassSettings(false);
    }
  };

  // Handle creating Encompass account
  const handleCreateEncompass = async () => {
    if (!linkedUserId) {
      toast.error('Must link user account first');
      return;
    }

    setIsCreatingEncompass(true);

    try {
      const { data: session } = await supabase.auth.getSession();

      const response = await fetch(
        encompassSyncUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session?.access_token}`
          },
          body: JSON.stringify({
            userId: linkedUserId,
            action: 'sync',
            organizationId: selectedEncompassOrgId || undefined,
            personaId: selectedEncompassPersonaId || undefined
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create Encompass account');
      }

      const encompassUsername = result.encompassId || result.username || '';
      const encompassPassword = result.password || '';

      // 'updated' means the account already exists — that is NOT proof of a
      // lost password, so never reset it from here. If a prior creation's
      // password save failed, provenance ('provisioned' with no saved
      // password) surfaces the explicit "Recover Temp Password" action instead.
      if (!encompassPassword && result.status === 'updated') {
        toast.error('This user already has an Encompass account — profile re-synced, password unchanged', {
          description: 'If a previous creation lost its temp password, use Recover Temp Password.',
          duration: 10000
        });
        return;
      }

      // Save provenance + temporary password. Provenance is written first so a
      // failed password save still leaves proof that WE created this account,
      // keeping the task blocked and the recovery action reachable after
      // reload. The onboarding email either needs this password or would
      // falsely claim pre-existing access without it.
      if (candidateId && encompassPassword) {
        let passwordSaved = false;
        try {
          const { error: provenanceError } = await supabase
            .from('recruits')
            .update({ encompass_access_mode: 'provisioned' })
            .eq('id', candidateId);
          if (provenanceError) {
            console.error('Failed to save Encompass provenance:', provenanceError);
            toast.warning('Encompass provenance failed to save', {
              description: 'If the password save below also fails, the recovery button will not reappear after a reload — retry this task.'
            });
          }

          const { error: updateError } = await supabase
            .from('recruits')
            .update({ encompass_temp_password: encompassPassword })
            .eq('id', candidateId);

          if (updateError) {
            console.error('Failed to save Encompass temp password:', updateError);
          } else {
            passwordSaved = true;
          }
        } catch (error) {
          console.error('Error saving Encompass temp password:', error);
        }

        if (!passwordSaved) {
          // Surface the recovery action immediately (and, via saved
          // provenance, after any reload)
          onTasksChange(
            tasks.map(t =>
              t.id === '4'
                ? {
                    ...t,
                    fieldValues: {
                      ...t.fieldValues,
                      encompass_access_mode: 'provisioned'
                    }
                  }
                : t
            )
          );
          toast.error('Encompass account created but the temp password failed to save', {
            description: `Username: ${encompassUsername}. Use Recover Temp Password to issue and save a new one.`,
            duration: 15000
          });
          return;
        }
      }

      // Mark Task 4 as completed with credentials (data persistence handled by edge function)
      onTasksChange(
        tasks.map(t =>
          t.id === '4'
            ? {
                ...t,
                status: 'completed',
                completedAt: new Date().toISOString(),
                fieldValues: {
                  encompass_username: encompassUsername,
                  encompass_password: encompassPassword,
                  encompass_access_mode: 'provisioned'
                }
              }
            : t
        )
      );

      toast.success('Encompass account created successfully', {
        description: `Username: ${encompassUsername}`
      });
    } catch (error) {
      console.error('Error creating Encompass account:', error);
      toast.error('Failed to create Encompass account', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreatingEncompass(false);
    }
  };

  // Explicit recovery for an account WE provisioned whose temp password was
  // never saved (provenance 'provisioned' + no stored password). Never runs
  // against existing-access Encompass accounts.
  const handleRecoverEncompassPassword = async () => {
    if (!linkedUserId || !candidateId) {
      toast.error('Must link user account first');
      return;
    }

    setIsCreatingEncompass(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await fetch(
        encompassSyncUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session?.access_token}`
          },
          body: JSON.stringify({ userId: linkedUserId, action: 'reset-password' })
        }
      );
      const result = await response.json();
      if (response.status === 403) {
        toast.error('Recovery requires an administrator', {
          description: 'Resetting another user\'s Encompass password is admin-only. Ask an admin to run Recover Temp Password.'
        });
        return;
      }
      if (!response.ok || !result.password) {
        throw new Error(result.error || 'Failed to issue a new Encompass temp password');
      }

      const { error: saveError } = await supabase
        .from('recruits')
        .update({ encompass_temp_password: result.password })
        .eq('id', candidateId);
      if (saveError) {
        throw new Error(`New password issued but saving failed: ${saveError.message}. Retry recovery.`);
      }

      // Username is display-only here; non-admin recruiters may not be able to
      // read the linked user row, and the saved password is what matters
      let encompassUsername = '';
      try {
        const { data: linkedUser } = await supabase
          .from('users')
          .select('encompass_user_id')
          .eq('id', linkedUserId)
          .single();
        encompassUsername = linkedUser?.encompass_user_id ?? '';
      } catch {
        // RLS may hide the row for non-admins; leave the username blank
      }

      onTasksChange(
        tasks.map(t =>
          t.id === '4'
            ? {
                ...t,
                status: 'completed' as const,
                completedAt: new Date().toISOString(),
                fieldValues: {
                  ...t.fieldValues,
                  encompass_username: encompassUsername || t.fieldValues?.['encompass_username'] || '',
                  encompass_password: result.password,
                  encompass_access_mode: 'provisioned'
                }
              }
            : t
        )
      );

      toast.success('New Encompass temp password issued and saved', {
        description: 'The user must change it on next Encompass login.'
      });
    } catch (error) {
      console.error('Error recovering Encompass temp password:', error);
      toast.error('Failed to recover Encompass temp password', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreatingEncompass(false);
    }
  };

  // Handle Xactus credentials request
  const handleRequestXactus = async () => {
    if (!candidateId) {
      toast.error('Candidate ID not found');
      return;
    }

    setIsRequestingXactus(true);

    try {
      // Get current recruit data
      const { data: recruit, error: recruitError } = await supabase
        .from('recruits')
        .select('first_name, last_name, nmls_number, phone, gmail_account, email')
        .eq('id', candidateId)
        .single();

      if (recruitError || !recruit) {
        throw new Error('Failed to load recruit data');
      }

      // Validate required fields
      if (!recruit.first_name || !recruit.last_name) {
        toast.error('First and last name are required');
        setIsRequestingXactus(false);
        return;
      }

      if (!recruit.nmls_number) {
        toast.error('NMLS number is required');
        setIsRequestingXactus(false);
        return;
      }

      if (!recruit.gmail_account) {
        toast.error('Gmail account required - complete Task 2 first');
        setIsRequestingXactus(false);
        return;
      }

      // Get company data
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user?.id) {
        toast.error('User not authenticated');
        setIsRequestingXactus(false);
        return;
      }

      const { data: userRecord } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userRecord?.organization_id) {
        toast.error('Organization not found');
        setIsRequestingXactus(false);
        return;
      }

      const { data: companyData, error: companyError } = await supabase
        .from('company_profiles')
        .select('company_name, company_nmls_id, headquarters_address')
        .eq('organization_id', userRecord.organization_id)
        .single();

      if (companyError || !companyData) {
        toast.error('Failed to load company information');
        setIsRequestingXactus(false);
        return;
      }

      // Get current user info for signature
      const { data: currentUser } = await supabase
        .from('users')
        .select('first_name, last_name, title')
        .eq('id', userData.user.id)
        .single();

      const address = companyData.headquarters_address;

      // Build form data
      const formData = {
        // Company Information
        companyName: companyData.company_name || '',
        mainCompany: 'X-25455',
        companyNmls: companyData.company_nmls_id || '',
        'companyAddress[addr_line1]': address?.street || '',
        'companyAddress[addr_line2]': address?.suite || '',
        'companyAddress[city]': address?.city || '',
        'companyAddress[state]': address?.state || '',
        'companyAddress[postal]': address?.zip || '',

        // Requester Information
        requesterName: 'Jennifer Steinberg',
        requesterTitle: 'VP',
        requesterPhone: '805-791-4548',
        requestorEmail: 'Jennifer@lendwisemtg.com',
        typeA: 'Tara French',
        strategicAccount: '',

        // Loan Officer Information (from recruit)
        'loName[first]': recruit.first_name,
        'loName[last]': recruit.last_name,
        loNmls: recruit.nmls_number,
        loPhone: recruit.phone || '',
        loEmail: recruit.gmail_account, // Use Gmail from Task 2

        // Signature/Authorization - Use logged-in user's info
        printYour: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`,
        'printedName[first]': currentUser?.first_name || '',
        'printedName[last]': currentUser?.last_name || '',
        title: currentUser?.title || 'Authorized User'
      };

      // Create pre-filled form URL
      const params = new URLSearchParams(formData);
      const xactusUrl = `https://xactus.jotform.com/242534618330048?${params.toString()}`;

      // Open pre-filled form in iframe
      setXactusModalOpen(true);
      setXactusIframeUrl(xactusUrl);

      toast.success('Form pre-filled! Please review and click Submit.', {
        duration: 5000
      });

      // Track request in database - mark as completed since request was submitted
      await supabase
        .from('recruits')
        .update({
          xactus_request_date: new Date().toISOString(),
          xactus_request_status: 'completed'
        })
        .eq('id', candidateId);

      // Mark Task 6 (Xactus) as completed
      onTasksChange(
        tasks.map(t =>
          t.id === '6'
            ? {
                ...t,
                status: 'completed',
                completedAt: new Date().toISOString(),
                fieldValues: {
                  request_date: new Date().toISOString()
                }
              }
            : t
        )
      );

    } catch (error) {
      console.error('Error requesting Xactus credentials:', error);
      toast.error('Failed to open Xactus form', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsRequestingXactus(false);
    }
  };

  // Handle task completion
  const handleCompleteTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
return;
}

    // Validate required fields
    const allFieldsFilled = task.fields?.every(field => {
      if (!field.required) {
return true;
}
      const value = task.fieldValues?.[field.id];
      return value && value.trim() !== '';
    });

    if (!allFieldsFilled) {
      // eslint-disable-next-line no-alert -- intentional native guardrail
      alert('Please fill in all required fields');
      return;
    }

    // Validate email domain for Gmail field
    const emailField = task.fields?.find(f => f.id === 'gmail_account');
    if (emailField) {
      const emailValue = task.fieldValues?.['gmail_account'];
      if (emailValue && !validateEmail(emailValue)) {
        // eslint-disable-next-line no-alert -- intentional native guardrail
        alert('Gmail address must be a valid @lendwisemtg.com email');
        return;
      }
    }

    // Persist to database (data only, no completion flags)
    if (candidateId) {
      try {
        const updates: Record<string, unknown> = {};

        if (taskId === '1') {
          updates.gmail_account = task.fieldValues?.['gmail_account'];
          updates.gmail_temp_password = task.fieldValues?.['temporary_password'];
        } else if (taskId === '3') {
          updates.optimal_username = task.fieldValues?.['optimal_username'];
        }

        if (Object.keys(updates).length > 0) {
          const { error } = await supabase
            .from('recruits')
            .update(updates)
            .eq('id', candidateId);

          if (error) {
throw error;
}
        }
      } catch (error) {
        console.error('Error persisting task completion:', error);
        toast.error('Failed to save task completion', {
          description: error instanceof Error ? error.message : 'Unknown error'
        });
        return;
      }
    }

    // Mark as completed in React state
    onTasksChange(
      tasks.map(t =>
        t.id === taskId
          ? {
              ...t,
              status: 'completed',
              completedAt: new Date().toISOString()
            }
          : t
      )
    );

    toast.success('Task completed successfully');
  };

  // Task 7: Handler for when all HR forms are validated
  const handleTask7Complete = () => {
    onTasksChange(
      tasks.map(t =>
        t.id === '7'
          ? {
              ...t,
              status: 'completed',
              completedAt: new Date().toISOString()
            }
          : t
      )
    );
  };

  // Handle saving NMLS authorization date
  const handleSaveNmlsDate = async () => {
    if (!candidateId || !nmlsAuthDate) {
      toast.error('Please select an authorization date');
      return;
    }

    setIsSavingNmls(true);

    try {
      const { data, error } = await supabase.rpc('set_recruit_nmls_authorization', {
        p_recruit_id: candidateId,
        p_date: nmlsAuthDate
      });

      if (error) {
throw error;
}

      const result = data as {
        marketingAuthorization?: unknown;
        delivery?: {
          id: string;
          emailType: 'partial_onboarding' | 'full_onboarding' | 'nmls_clearance';
          status: string;
        } | null;
      } | null;

      setNmlsSavedDate(nmlsAuthDate);

      // Mark Task 5 as completed
      onTasksChange(
        tasks.map(t =>
          t.id === '5'
            ? {
                ...t,
                status: 'completed',
                completedAt: new Date().toISOString(),
                fieldValues: {
                  nmls_authorization_date: nmlsAuthDate
                }
              }
            : t
        )
      );

      toast.success('NMLS authorization date saved successfully');

      window.dispatchEvent(new CustomEvent('lendwise:nmls-authorization-changed', {
        detail: { userId: linkedUserId, authorized: true }
      }));

      if (result?.delivery?.id) {
        const emailResult = await RecruitmentInvitationService.sendOnboardingEmail({
          deliveryId: result.delivery.id
        });

        if (emailResult.success) {
          toast.success(
            result.delivery.emailType === 'nmls_clearance'
              ? 'NMLS clearance email sent'
              : 'Full onboarding email sent'
          );
        } else {
          toast.warning('Authorization saved, but the onboarding email was not sent', {
            description: `${emailResult.error || 'The delivery failed.'} You can retry it from the onboarding email action.`
          });
        }
      }

      await onEmailDeliveryChange?.();

      // Send NMLS authorization celebration notification (non-blocking)
      try {
        const { data: recruitData } = await supabase
          .from('recruits')
          .select(`
            first_name,
            last_name,
            nmls_number,
            state_licenses,
            recruiter_id,
            recruiter:users!recruiter_id(full_name)
          `)
          .eq('id', candidateId)
          .single();

        if (recruitData) {
          recruiterNotificationService.notifyNmlsAuthorizationGranted({
            recruitId: candidateId,
            loFirstName: recruitData.first_name || '',
            loLastName: recruitData.last_name || '',
            nmlsNumber: recruitData.nmls_number || '',
            stateLicenses: recruitData.state_licenses || [],
            authorizationDate: nmlsAuthDate,
            recruiterName: (recruitData.recruiter as { full_name?: string } | null)?.full_name,
            recruiterId: recruitData.recruiter_id || undefined
          }).catch(err => log.warn('NMLS authorization notification failed (non-blocking)', { error: err }));
        }
      } catch (notifyError) {
        // Non-blocking - don't fail the main operation
        log.warn('Failed to send NMLS authorization notification', { error: notifyError });
      }
    } catch (error) {
      log.error('Error saving NMLS authorization date', { metadata: { candidateId } }, error as Error);
      toast.error('Failed to save authorization date', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSavingNmls(false);
    }
  };

  // Handle clearing NMLS authorization date
  const handleClearNmlsDate = async () => {
    if (!candidateId) {
return;
}

    // eslint-disable-next-line no-alert -- intentional native confirm
    const confirmed = window.confirm(
      'Are you sure you want to clear the NMLS authorization date? This will mark the task as incomplete.'
    );

    if (!confirmed) {
return;
}

    setIsSavingNmls(true);

    try {
      const { error } = await supabase.rpc('set_recruit_nmls_authorization', {
        p_recruit_id: candidateId,
        p_date: null
      });

      if (error) {
throw error;
}

      setNmlsAuthDate('');
      setNmlsSavedDate(null);

      // Mark Task 5 as pending
      onTasksChange(
        tasks.map(t =>
          t.id === '5'
            ? {
                ...t,
                status: 'pending',
                completedAt: undefined,
                fieldValues: {}
              }
            : t
        )
      );

      toast.success('NMLS authorization date cleared');
      window.dispatchEvent(new CustomEvent('lendwise:nmls-authorization-changed', {
        detail: { userId: linkedUserId, authorized: false }
      }));
      await onEmailDeliveryChange?.();
    } catch (error) {
      log.error('Error clearing NMLS authorization date', { metadata: { candidateId } }, error as Error);
      toast.error('Failed to clear authorization date', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSavingNmls(false);
    }
  };

  // Handle creating Gmail account via Google Workspace API
  const handleCreateGmail = async () => {
    if (!candidateId) {
      toast.error('Candidate ID not found');
      return;
    }

    setIsCreatingGmail(true);
    setGmailError(null); // Clear any previous error

    try {
      // Get recruit first name and last name
      const { data: recruit, error: recruitError } = await supabase
        .from('recruits')
        .select('first_name, last_name')
        .eq('id', candidateId)
        .single();

      if (recruitError || !recruit) {
        throw new Error('Failed to load recruit data');
      }

      if (!recruit.first_name || !recruit.last_name) {
        toast.error('First and last name are required');
        setIsCreatingGmail(false);
        return;
      }

      // Call the workspace service to create Gmail account
      const result = await createGmailAccount(
        candidateId,
        recruit.first_name,
        recruit.last_name
      );

      if (!result.success || !result.email || !result.tempPassword) {
        // Handle specific error codes with user-friendly messages
        const errorCode = result.errorCode || 'UNKNOWN';
        const errorMessages: Record<WorkspaceErrorCode, { title: string; description: string }> = {
          LICENSE_LIMIT: {
            title: 'License limit reached',
            description: 'Add more Google Workspace licenses at admin.google.com → Billing → Subscriptions.'
          },
          EMAIL_EXISTS: {
            title: 'Email already exists',
            description: 'This email address is already in use in Google Workspace.'
          },
          PERMISSION_DENIED: {
            title: 'Permission denied',
            description: 'Service account lacks permissions. Contact administrator.'
          },
          CONFIG_ERROR: {
            title: 'Configuration error',
            description: 'Google Workspace integration not properly configured.'
          },
          UNKNOWN: {
            title: 'Failed to create Gmail account',
            description: result.error || 'An unexpected error occurred.'
          }
        };

        const errorInfo = errorMessages[errorCode];

        // Set error state for inline display
        setGmailError({
          message: errorInfo.description,
          code: errorCode
        });

        // Show toast with longer duration for important errors
        toast.error(errorInfo.title, {
          description: errorInfo.description,
          duration: errorCode === 'LICENSE_LIMIT' ? 10000 : 5000
        });

        return;
      }

      // Store credentials for display
      setGmailCredentials({
        email: result.email,
        tempPassword: result.tempPassword
      });

      // Update recruitGmailAccount so Task 2 is immediately available (no refresh needed)
      setRecruitGmailAccount(result.email);

      // Mark Task 1 as completed; Xactus (Task 6) was locked at drawer init
      // while Gmail was missing, so unlock it in-session too
      onTasksChange(
        tasks.map(t => {
          if (t.id === '1') {
            return {
              ...t,
              status: 'completed' as const,
              completedAt: new Date().toISOString(),
              fieldValues: {
                gmail_account: result.email,
                temporary_password: result.tempPassword
              }
            };
          }
          if (t.id === '6' && t.status === 'locked') {
            return { ...t, status: 'pending' as const };
          }
          return t;
        })
      );

      toast.success('Gmail account created successfully', {
        description: result.email
      });
    } catch (error) {
      console.error('Error creating Gmail account:', error);
      setGmailError({
        message: error instanceof Error ? error.message : 'Unknown error',
        code: 'UNKNOWN'
      });
      toast.error('Failed to create Gmail account', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsCreatingGmail(false);
    }
  };

  // Open the Link Existing Gmail picker and load eligible portal users via the
  // authorized edge function (client users queries are RLS-limited for
  // non-admin Talent Pipeline users)
  const handleOpenLinkExisting = async () => {
    if (!candidateId) {
      toast.error('Candidate ID not found');
      return;
    }
    setLinkModalOpen(true);
    setSelectedLinkUserId(null);
    setLinkSearch('');
    setLoadingLinkableUsers(true);
    try {
      const { users, error } = await getLinkableUsers(candidateId);
      if (error) {
        throw new Error(error);
      }
      setLinkableUsers(users);
    } catch (error) {
      console.error('Failed to load linkable users:', error);
      toast.error('Failed to load portal users', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
      setLinkModalOpen(false);
    } finally {
      setLoadingLinkableUsers(false);
    }
  };

  // Link the recruit to an existing portal user's Gmail account
  const handleLinkExistingGmail = async () => {
    if (!candidateId || !selectedLinkUserId) {
      return;
    }
    const selectedUser = linkableUsers.find(user => user.id === selectedLinkUserId);
    if (!selectedUser) {
      return;
    }

    setIsLinkingExisting(true);
    try {
      const result = await linkRecruitToUser(candidateId, selectedLinkUserId);
      if (!result.success) {
        toast.error('Failed to link existing Gmail', {
          description: result.error || 'Unknown error'
        });
        return;
      }

      const linkedEmail = result.userEmail || selectedUser.email;

      // The link function reports the user's Encompass ID (a client-side users
      // query is RLS-hidden from non-admin recruiters); Task 4 completes from it
      const encompassUserId = result.encompassUserId ?? null;

      setRecruitGmailAccount(linkedEmail);
      setLinkedUserId(selectedLinkUserId);
      setLinkedExistingEmail(linkedEmail);
      setLinkModalOpen(false);

      onTasksChange(
        tasks.map(t => {
          if (t.id === '1') {
            return {
              ...t,
              status: 'completed' as const,
              completedAt: new Date().toISOString(),
              fieldValues: {
                gmail_account: linkedEmail,
                temporary_password: '',
                access_mode: 'existing'
              }
            };
          }
          if (t.id === '2') {
            return {
              ...t,
              status: 'completed' as const,
              completedAt: new Date().toISOString(),
              fieldValues: {
                linked_user_id: selectedLinkUserId,
                linked_user_email: linkedEmail
              }
            };
          }
          if (t.id === '4' && encompassUserId) {
            return {
              ...t,
              status: 'completed' as const,
              completedAt: new Date().toISOString(),
              fieldValues: {
                encompass_username: encompassUserId,
                encompass_password: '',
                encompass_access_mode: 'existing'
              }
            };
          }
          // Xactus (Task 6) was locked at drawer init while Gmail was missing
          if (t.id === '6' && t.status === 'locked') {
            return { ...t, status: 'pending' as const };
          }
          return t;
        })
      );

      toast.success('Linked to existing Gmail account', {
        description: `${selectedUser.fullName} — ${linkedEmail}. Existing access; no temporary password.`
      });
      await onEmailDeliveryChange?.();
    } catch (error) {
      console.error('Error linking existing Gmail:', error);
      toast.error('Failed to link existing Gmail', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsLinkingExisting(false);
    }
  };

  // Helper to copy text to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <div className="dw-sec-title" style={{ marginBottom: 4 }}>Onboarding Tasks</div>
          <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>
            Track and manage onboarding progress for this recruit
          </p>
        </div>
        <button type="button" className="btn-sm">
          <Plus />
          Add Task
        </button>
      </div>

      {/* Progress Summary Card */}
      <div className="panel" style={{ padding: 16, background: 'var(--gold-wash)', borderColor: 'var(--gold-soft)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--green-900)', margin: 0 }}>Overall Progress</p>
            <p style={{ fontSize: 11.5, color: 'var(--ink-soft)', marginTop: 4 }}>
              {completedTasks} of {totalTasks} tasks completed
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--green-900)' }}>{progressPercent}%</div>
            <span
              className="badge"
              style={progressPercent === 100 ? { background: 'var(--green-700)', color: '#EAE3CF' } : { background: '#F6EEDA', color: '#7E6426' }}
            >
              {progressPercent === 100 ? 'Complete' : 'In Progress'}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{ marginTop: 12, width: '100%', background: 'var(--line-soft)', borderRadius: 999, height: 8 }}>
          <div
            style={{ background: 'var(--green-700)', height: 8, borderRadius: 999, transition: 'all .5s', width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">Tasks</h4>
          <span className="text-xs text-gray-500">
            {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}
          </span>
        </div>

        {/* Task Cards */}
        {tasks.map(task => (
          <Card
            key={task.id}
            className="p-4"
            style={task.status === 'completed' ? { background: '#E9EFE7', borderColor: '#BFD8C6' } : undefined}
          >
            {/* Task Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="mt-0.5">
                  {task.status === 'completed' ? (
                    <CheckCircle2 size={20} style={{ color: '#2E8255' }} />
                  ) : (
                    <Circle size={20} style={{ color: 'var(--lw-muted)' }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h5 style={{ fontSize: 14, fontWeight: 600, color: 'var(--green-900)', margin: 0 }}>{task.title}</h5>
                    <span className="badge" style={{ background: '#EEEADD', color: '#6F6A57', height: 21, fontSize: 11 }}>
                      {task.category}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--lw-muted)', marginTop: 4 }}>{task.description}</p>
                </div>
              </div>
              <span
                className="badge"
                style={task.status === 'completed' ? { background: 'var(--green-700)', color: '#EAE3CF' } : { background: '#EEEADD', color: '#6F6A57' }}
              >
                {task.status === 'completed' ? 'Completed' : 'Pending'}
              </span>
            </div>

            {/* Task 1: Create Gmail Account (Special UI) */}
            {task.id === '1' && task.status === 'pending' && (
              <div className="space-y-3 mt-4 pl-8">
                <p className="text-xs text-gray-600">
                  Create a Gmail account for this recruit. The system will try <strong>firstname@lendwisemtg.com</strong> first,
                  then <strong>firstnamelastname@lendwisemtg.com</strong> if the first is taken.
                  If they already have a provisioned account, use <strong>Link Existing Gmail</strong> instead.
                </p>

                {/* Error Alert */}
                {gmailError && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      borderRadius: 9,
                      padding: 12,
                      ...(gmailError.code === 'LICENSE_LIMIT'
                        ? { background: '#F7F0DC', border: '1px solid #E4D2A0' }
                        : { background: '#F6EBE5', border: '1px solid #E3C4B6' })
                    }}
                  >
                    <AlertCircle
                      size={16}
                      style={{ flexShrink: 0, marginTop: 1, color: gmailError.code === 'LICENSE_LIMIT' ? '#8A6420' : 'var(--danger)' }}
                    />
                    <div className="flex-1">
                      <p style={{ fontSize: 13, fontWeight: 600, margin: 0, color: gmailError.code === 'LICENSE_LIMIT' ? '#7E6426' : '#7E3B27' }}>
                        {gmailError.code === 'LICENSE_LIMIT' ? 'License Limit Reached' : 'Error Creating Account'}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 4, color: gmailError.code === 'LICENSE_LIMIT' ? '#8A6420' : '#8A5A47' }}>
                        {gmailError.message}
                      </p>
                      {gmailError.code === 'LICENSE_LIMIT' && (
                        <a
                          href="https://admin.google.com/ac/billing/subscriptions"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#8A6420', textDecoration: 'underline', marginTop: 8 }}
                        >
                          <ExternalLink size={12} />
                          Open Google Admin Billing
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {gmailCredentials ? (
                  <div className="space-y-3">
                    <div style={{ background: '#E9EFE7', border: '1px solid #BFD8C6', borderRadius: 9, padding: 12 }}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle size={16} style={{ color: '#2E8255' }} />
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#246042', margin: 0 }}>Gmail Account Created</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between" style={{ background: 'var(--lw-card)', borderRadius: 7, padding: 8 }}>
                          <div>
                            <p style={{ fontSize: 11, color: 'var(--lw-muted)', margin: 0 }}>Email</p>
                            <p style={{ fontSize: 13, fontFamily: 'ui-monospace, monospace', margin: 0 }}>{gmailCredentials.email}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(gmailCredentials.email, 'Email')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between" style={{ background: 'var(--lw-card)', borderRadius: 7, padding: 8 }}>
                          <div>
                            <p style={{ fontSize: 11, color: 'var(--lw-muted)', margin: 0 }}>Temporary Password</p>
                            <p style={{ fontSize: 13, fontFamily: 'ui-monospace, monospace', margin: 0 }}>{gmailCredentials.tempPassword}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(gmailCredentials.tempPassword, 'Password')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <p style={{ fontSize: 11.5, color: '#3A6B4E', marginTop: 8 }}>
                        User will be prompted to change password on first login.
                      </p>
                    </div>
                  </div>
                ) : linkedExistingEmail ? (
                  <div style={{ background: '#E9EFE7', border: '1px solid #BFD8C6', borderRadius: 9, padding: 12 }}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={16} style={{ color: '#2E8255' }} />
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#246042', margin: 0 }}>Existing Gmail Account Linked</p>
                    </div>
                    <div className="flex items-center justify-between" style={{ background: 'var(--lw-card)', borderRadius: 7, padding: 8 }}>
                      <div>
                        <p style={{ fontSize: 11, color: 'var(--lw-muted)', margin: 0 }}>Email</p>
                        <p style={{ fontSize: 13, fontFamily: 'ui-monospace, monospace', margin: 0 }}>{linkedExistingEmail}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(linkedExistingEmail, 'Email')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p style={{ fontSize: 11.5, color: '#3A6B4E', marginTop: 8 }}>
                      Existing account — they already have access with their current credentials. No temporary password.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      size="sm"
                      onClick={handleCreateGmail}
                      disabled={isCreatingGmail || isLinkingExisting}
                      className="flex-1"
                    >
                      {isCreatingGmail ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Gmail Account...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Create Gmail Account
                        </>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOpenLinkExisting}
                      disabled={isCreatingGmail || isLinkingExisting}
                      className="flex-1"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Link Existing Gmail
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Task Fields (only show if pending and not Task 1) */}
            {task.status === 'pending' && task.fields && task.fields.length > 0 && task.id !== '1' && (
              <div className="space-y-3 mt-4 pl-8">
                <div className="grid grid-cols-2 gap-3">
                  {task.fields.map(field => (
                    <div key={field.id} className="space-y-1.5">
                      <Label htmlFor={`${task.id}-${field.id}`} className="text-xs font-medium">
                        {field.label}
                        {field.required && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>}
                      </Label>
                      <div className="relative">
                        {field.id === 'gmail_account' && (
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        )}
                        {field.id === 'optimal_username' && (
                          <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        )}
                        {field.id === 'temporary_password' && (
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        )}
                        <Input
                          id={`${task.id}-${field.id}`}
                          type={field.type}
                          placeholder={field.placeholder}
                          value={task.fieldValues?.[field.id] || ''}
                          onChange={e => handleFieldChange(task.id, field.id, e.target.value)}
                          className={`${['gmail_account', 'temporary_password', 'optimal_username'].includes(field.id) ? 'pl-10' : ''}`}
                          autoComplete={field.id === 'temporary_password' ? 'new-password' : 'off'}
                          data-form-type="other"
                        />
                      </div>
                      {field.validation?.message &&
                        task.fieldValues?.[field.id] &&
                        field.id === 'gmail_account' &&
                        !validateEmail(task.fieldValues[field.id]) && (
                          <div className="flex items-center gap-1" style={{ fontSize: 12, color: 'var(--danger)' }}>
                            <AlertCircle className="h-3 w-3" />
                            <span>{field.validation.message}</span>
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                {/* Complete Button */}
                <div className="pt-2">
                  <Button
                    size="sm"
                    onClick={() => handleCompleteTask(task.id)}
                    className="w-full"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Complete
                  </Button>
                </div>
              </div>
            )}

            {/* Task 2: Link User Account (New Role Selection UI) */}
            {task.id === '2' && task.status === 'pending' && (
              <div className="space-y-4 mt-4 pl-8">
                {/* Check if Task 1 (Gmail) is completed */}
                {tasks.find(t => t.id === '1')?.status !== 'completed' && !recruitGmailAccount ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 9, padding: 12 }}>
                    <AlertCircle size={16} style={{ color: '#8A6420', flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 12, color: '#7E6426', margin: 0 }}>
                      Complete "Create Gmail Account" (Task 1) first before linking user account.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Gmail Account Display */}
                    <div className="panel" style={{ padding: 12 }}>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-xs text-gray-500">Gmail Account</p>
                          <p className="text-sm font-mono font-medium">
                            {recruitGmailAccount || tasks.find(t => t.id === '1')?.fieldValues?.gmail_account || 'Not set'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Role Selection */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">
                        Assign Roles <span style={{ color: 'var(--danger)' }}>*</span>
                      </Label>
                      <div className="bg-white border border-gray-200 rounded-md p-3 space-y-2">
                        {AVAILABLE_ROLES.map(role => (
                          <div key={role.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`role-${role.value}`}
                              checked={selectedRoles.includes(role.value)}
                              onCheckedChange={(checked) => handleRoleToggle(role.value, checked as boolean)}
                            />
                            <label
                              htmlFor={`role-${role.value}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {role.label}
                            </label>
                          </div>
                        ))}
                      </div>
                      {selectedRoles.length === 0 && (
                        <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>Select at least one role</p>
                      )}
                    </div>

                    {/* What this will do */}
                    <div className="panel" style={{ padding: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--green-900)', margin: '0 0 8px' }}>This will:</p>
                      <ul style={{ fontSize: 12, color: 'var(--ink-soft)', display: 'flex', flexDirection: 'column', gap: 4, margin: 0, listStyle: 'none', padding: 0 }}>
                        <li style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={12} style={{ color: '#2E8255' }} />
                          Create user account with selected roles
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={12} style={{ color: '#2E8255' }} />
                          Set user as Active (can log in immediately)
                        </li>
                        <li style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircle2 size={12} style={{ color: '#2E8255' }} />
                          Link recruit to user account
                        </li>
                      </ul>
                    </div>

                    {/* Create / Link Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        size="sm"
                        onClick={handleCreateUserFromRecruit}
                        disabled={selectedRoles.length === 0 || isCreatingUser || isLinkingExisting || !recruitGmailAccount}
                        className="flex-1"
                      >
                        {isCreatingUser ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Creating User Account...
                          </>
                        ) : (
                          <>
                            <User className="h-4 w-4 mr-2" />
                            Create & Link User Account
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleOpenLinkExisting}
                        disabled={isCreatingUser || isLinkingExisting}
                        className="flex-1"
                      >
                        <User className="h-4 w-4 mr-2" />
                        Link Existing User
                      </Button>
                    </div>
                    <p style={{ fontSize: 11.5, color: 'var(--lw-muted)', margin: 0 }}>
                      Already a portal user? Link Existing User connects this recruit without creating a new account.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Task 4: Create Encompass Account (Special UI - Conditional) */}
            {task.id === '4' && task.status === 'pending' && (
              <div className="space-y-3 mt-4 pl-8">
                {tasks.find(t => t.id === '2')?.status !== 'completed' ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 9, padding: 12 }}>
                    <AlertCircle size={16} style={{ color: '#8A6420', flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 12, color: '#7E6426', margin: 0 }}>
                      Complete "Link User Account" task first before creating Encompass account.
                    </p>
                  </div>
                ) : task.fieldValues?.['encompass_access_mode'] === 'provisioned' ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 9, padding: 12 }}>
                      <AlertCircle size={16} style={{ color: '#8A6420', flexShrink: 0, marginTop: 1 }} />
                      <p style={{ fontSize: 12, color: '#7E6426', margin: 0 }}>
                        An Encompass account was created for this recruit, but no temporary password is saved.
                        Issue a new one so the onboarding email can include real credentials.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleRecoverEncompassPassword}
                      disabled={isCreatingEncompass || !linkedUserId}
                      className="w-full"
                    >
                      {isCreatingEncompass ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Recovering Temp Password...
                        </>
                      ) : (
                        <>
                          <Lock className="h-4 w-4 mr-2" />
                          Recover Temp Password
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-gray-600">
                      Create Encompass LOS user account for loan processing. This will generate a username and temporary password.
                    </p>

                    {/* Organization and Persona Selection */}
                    {loadingEncompassSettings ? (
                      <div className="flex items-center gap-2 text-muted-foreground py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-xs">Loading organization settings...</span>
                      </div>
                    ) : (
                      <div className="space-y-3 panel" style={{ padding: 12 }}>
                        {/* Organization Dropdown */}
                        <div className="space-y-1.5">
                          <Label htmlFor="encompass-org" className="text-xs font-medium">
                            Organization
                          </Label>
                          <Select
                            value={selectedEncompassOrgId}
                            onValueChange={setSelectedEncompassOrgId}
                          >
                            <SelectTrigger id="encompass-org" className="h-8 text-xs">
                              <SelectValue placeholder="Select organization..." />
                            </SelectTrigger>
                            <SelectContent className="lw-ds">
                              {encompassOrganizations.map((org) => (
                                <SelectItem key={org.id} value={org.id} className="text-xs">
                                  {org.path}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Persona Dropdown */}
                        <div className="space-y-1.5">
                          <Label htmlFor="encompass-persona" className="text-xs font-medium">
                            Persona
                          </Label>
                          <Select
                            value={selectedEncompassPersonaId}
                            onValueChange={setSelectedEncompassPersonaId}
                          >
                            <SelectTrigger id="encompass-persona" className="h-8 text-xs">
                              <SelectValue placeholder="Select persona..." />
                            </SelectTrigger>
                            <SelectContent className="lw-ds">
                              {encompassPersonas.map((persona) => (
                                <SelectItem key={persona.id} value={persona.id} className="text-xs">
                                  {persona.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}

                    <Button
                      size="sm"
                      onClick={handleCreateEncompass}
                      disabled={isCreatingEncompass || !linkedUserId}
                      className="w-full"
                    >
                      {isCreatingEncompass ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Encompass Account...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Create Encompass Account
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Task 5: NMLS Authorization (Date Input) */}
            {task.id === '5' && task.status !== 'completed' && (
              <div className="space-y-3 mt-4 pl-8">
                <p className="text-xs text-gray-600">
                  Record the date when NMLS granted authorization for this loan officer to conduct business.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="nmls-auth-date" className="text-xs font-medium">
                    Authorization Date <span style={{ color: 'var(--danger)' }}>*</span>
                  </Label>
                  <Input
                    id="nmls-auth-date"
                    type="date"
                    value={nmlsAuthDate}
                    onChange={(e) => setNmlsAuthDate(e.target.value)}
                    className="w-full"
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500">
                    Select the date NMLS granted authorization (cannot be in the future)
                  </p>
                </div>

                {nmlsSavedDate ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2" style={{ fontSize: 12, color: '#2E8255' }}>
                      <CheckCircle className="h-4 w-4" />
                      <span>Saved on {parseAsLocalDate(nmlsSavedDate).toLocaleDateString()}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={handleClearNmlsDate}
                      disabled={isSavingNmls}
                      className="w-full"
                    >
                      {isSavingNmls ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Clearing...
                        </>
                      ) : (
                        <>Clear Date</>
                      )}
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleSaveNmlsDate}
                    disabled={!nmlsAuthDate || isSavingNmls}
                    className="w-full"
                  >
                    {isSavingNmls ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Save Authorization Date
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Task 6: Request Xactus Credentials (Special UI - Conditional) */}
            {task.id === '6' && task.status !== 'completed' && (
              <div className="space-y-3 mt-4 pl-8">
                {task.status === 'locked' || tasks.find(t => t.id === '1')?.status !== 'completed' ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#F7F0DC', border: '1px solid #E4D2A0', borderRadius: 9, padding: 12 }}>
                    <Lock size={16} style={{ color: '#8A6420', flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 12, color: '#7E6426', margin: 0 }}>
                      Complete "Gmail Account Assigned" (Task 1) first. Xactus requires the company Gmail address.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-600">
                      Submit a pre-filled request form to Xactus for billing verification credentials. The form will open with all recruit and company information auto-populated.
                    </p>

                    <Button
                      size="sm"
                      onClick={handleRequestXactus}
                      disabled={isRequestingXactus}
                      className="w-full"
                    >
                      {isRequestingXactus ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Opening Xactus Form...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Request Xactus Credentials
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Task 7: Identification Documents */}
            {task.id === '7' && task.status !== 'completed' && candidateId && (
              <div className="mt-4 pl-8">
                <IDDocumentsChecklist
                  recruitId={candidateId}
                  onTaskComplete={handleTask7Complete}
                />
              </div>
            )}


            {/* Completed Task Info */}
            {task.status === 'completed' && (
              <div className="mt-4 pl-8 space-y-2">
                {/* Task 1: Show Gmail credentials with copy buttons */}
                {task.id === '1' && task.fieldValues && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700">Email:</span>
                      <span className="text-xs text-gray-600 font-mono">
                        {task.fieldValues?.['gmail_account'] || 'Not provided'}
                      </span>
                      {task.fieldValues?.['gmail_account'] && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(task.fieldValues!['gmail_account'], 'Email')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    {task.fieldValues?.['access_mode'] === 'existing' ? (
                      <div className="text-xs" style={{ color: '#3A6B4E' }}>
                        Existing account — they already have access with their current credentials. No temporary password.
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700">Temp Password:</span>
                        <span className="text-xs text-gray-600 font-mono">
                          {task.fieldValues?.['temporary_password'] || 'Not provided'}
                        </span>
                        {task.fieldValues?.['temporary_password'] && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(task.fieldValues!['temporary_password'], 'Password')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Task 2: Show linked user info */}
                {task.id === '2' && task.fieldValues && (
                  <>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Linked User:</span>{' '}
                      <span className="text-gray-600">
                        {task.fieldValues['linked_user_email'] || 'User linked'}
                      </span>
                    </div>
                    {task.fieldValues['roles'] && (
                      <div className="text-xs">
                        <span className="font-medium text-gray-700">Roles:</span>{' '}
                        <span className="text-gray-600">
                          {task.fieldValues['roles']}
                        </span>
                      </div>
                    )}
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">User ID:</span>{' '}
                      <span className="text-gray-600 font-mono text-[10px]">
                        {task.fieldValues['linked_user_id'] || ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#2E8255', marginTop: 4 }}>
                      User can log in immediately with their Gmail credentials
                    </div>
                  </>
                )}

                {/* Task 4: Show Encompass credentials */}
                {task.id === '4' && task.fieldValues && (
                  <>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Encompass Username:</span>{' '}
                      <span className="text-gray-600 font-mono">
                        {task.fieldValues['encompass_username'] || 'Not available'}
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Temporary Password:</span>{' '}
                      <span className="text-gray-600 font-mono">
                        {task.fieldValues['encompass_password'] || 'Not available'}
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Client ID:</span>{' '}
                      <span className="text-gray-600 font-mono">
                        BE11391385
                      </span>
                    </div>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Download Encompass Desktop:</span>{' '}
                      <a
                        href="https://download.elliemae.com/encompass/install/smartclient/enc360/EncompassDesktop.exe"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--green-700)', textDecoration: 'underline' }}
                      >
                        EncompassDesktop.exe
                      </a>
                    </div>
                  </>
                )}

                {/* Task 5: Show NMLS authorization date */}
                {task.id === '5' && task.fieldValues && (
                  <>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Authorization Date:</span>{' '}
                      <span className="text-gray-600">
                        {task.fieldValues['nmls_authorization_date']
                          ? parseAsLocalDate(task.fieldValues['nmls_authorization_date']).toLocaleDateString()
                          : 'Not available'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--lw-muted)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Info size={12} style={{ flex: 'none' }} /> NMLS authorization granted to conduct business
                    </div>

                    {/* Clear Authorization Date button */}
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleClearNmlsDate}
                        disabled={isSavingNmls}
                        className="w-full"
                      >
                        {isSavingNmls ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Clearing...
                          </>
                        ) : (
                          <>Clear Authorization Date</>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                {/* Task 6: Show Xactus request info */}
                {task.id === '6' && task.fieldValues && (
                  <>
                    <div className="text-xs">
                      <span className="font-medium text-gray-700">Request Date:</span>{' '}
                      <span className="text-gray-600">
                        {task.fieldValues['request_date']
                          ? parseAsLocalDate(task.fieldValues['request_date']).toLocaleString()
                          : 'Not available'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--lw-muted)', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--line-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Info size={12} style={{ flex: 'none' }} /> Xactus credentials request submitted via JotForm
                    </div>
                  </>
                )}

                {/* Task 7: Show HR forms completion summary */}
                {task.id === '7' && (
                  <>
                    <div className="space-y-3">
                      <div style={{ background: '#E9EFE7', border: '1px solid #BFD8C6', borderRadius: 9, padding: 12 }}>
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle size={16} style={{ color: '#2E8255' }} />
                          <p style={{ fontSize: 13, fontWeight: 600, color: '#246042', margin: 0 }}>All HR Forms Validated</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#3A6B4E' }}>
                          <div className="flex items-center gap-2">
                            <span style={{ width: 48, fontWeight: 600 }}>W-4:</span>
                            <span>Employee's Withholding Certificate</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span style={{ width: 48, fontWeight: 600 }}>I-9:</span>
                            <span>Employment Eligibility Verification</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span style={{ width: 48, fontWeight: 600 }}>W-9:</span>
                            <span>Request for Taxpayer Identification</span>
                          </div>
                        </div>
                      </div>
                      <p style={{ fontSize: 11.5, color: 'var(--lw-muted)', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                        <CheckCircle2 size={12} style={{ color: '#2E8255', flex: 'none' }} />
                        All required tax and HR documentation collected and verified
                      </p>
                    </div>
                  </>
                )}

                {/* Standard fields for Task 3 */}
                {task.id === '3' && task.fields && task.fields.length > 0 && task.fieldValues && (
                  <>
                    {task.fields.map(field => (
                      <div key={field.id} className="text-xs">
                        <span className="font-medium text-gray-700">{field.label}:</span>{' '}
                        <span className="text-gray-600">
                          {task.fieldValues?.[field.id] || 'Not provided'}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {task.completedAt && (
                  <div className="text-xs text-gray-500 pt-1">
                    Completed: {parseAsLocalDate(task.completedAt).toLocaleString()}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Link Existing Gmail Modal */}
      {linkModalOpen && (
        <LwModal
          eyebrow="Onboarding"
          title="Link Existing Gmail"
          sub="Connect this recruit to a portal user who already has a provisioned @lendwisemtg.com account."
          onClose={() => !isLinkingExisting && setLinkModalOpen(false)}
          footer={
            <>
              <button
                className="btn-sm"
                type="button"
                onClick={() => setLinkModalOpen(false)}
                disabled={isLinkingExisting}
              >
                Cancel
              </button>
              <button
                className="btn-sm solid"
                type="button"
                onClick={handleLinkExistingGmail}
                disabled={!selectedLinkUserId || isLinkingExisting || loadingLinkableUsers}
              >
                {isLinkingExisting ? <Loader2 className="animate-spin" /> : <User />}
                {isLinkingExisting ? 'Linking…' : 'Link Existing Gmail'}
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="panel" style={{ display: 'flex', gap: 10, padding: 12, alignItems: 'flex-start' }}>
              <Info size={16} style={{ color: 'var(--lw-muted)', flex: 'none', marginTop: 1 }} />
              <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', margin: 0 }}>
                This links the recruit to the selected user's Gmail account and portal login.
                No accounts, roles, or passwords are created or changed — the welcome email will
                say they already have access.
              </p>
            </div>
            <Input
              placeholder="Search by name or email…"
              value={linkSearch}
              onChange={(event) => setLinkSearch(event.target.value)}
              disabled={loadingLinkableUsers}
            />
            {loadingLinkableUsers ? (
              <div className="flex items-center gap-2" style={{ padding: 12, color: 'var(--lw-muted)', fontSize: 13 }}>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading portal users…
              </div>
            ) : (
              <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 9 }}>
                {linkableUsers
                  .filter(user => {
                    const query = linkSearch.trim().toLowerCase();
                    if (!query) {
                      return true;
                    }
                    return user.fullName.toLowerCase().includes(query) || user.email.toLowerCase().includes(query);
                  })
                  .map(user => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => setSelectedLinkUserId(user.id)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        border: 'none',
                        borderBottom: '1px solid var(--line-soft)',
                        cursor: 'pointer',
                        background: selectedLinkUserId === user.id ? 'var(--gold-wash)' : 'transparent'
                      }}
                    >
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--green-900)' }}>{user.fullName}</span>
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--lw-muted)', fontFamily: 'ui-monospace, monospace' }}>{user.email}</span>
                    </button>
                  ))}
                {linkableUsers.filter(user => {
                  const query = linkSearch.trim().toLowerCase();
                  if (!query) {
                    return true;
                  }
                  return user.fullName.toLowerCase().includes(query) || user.email.toLowerCase().includes(query);
                }).length === 0 && (
                  <p style={{ padding: 12, fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>
                    No unlinked portal users match{linkSearch.trim() ? ` "${linkSearch.trim()}"` : ''}.
                  </p>
                )}
              </div>
            )}
          </div>
        </LwModal>
      )}

      {/* Xactus Submission Modal */}
      {xactusModalOpen && (
        <LwModal
          size="wide"
          eyebrow="Onboarding"
          title="Review and Submit Xactus Credentials Request"
          sub="Please review the pre-filled form and click Submit at the bottom."
          onClose={() => setXactusModalOpen(false)}
        >
          <div className="panel" style={{ display: 'flex', gap: 10, padding: 12, alignItems: 'flex-start' }}>
            <CheckCircle size={16} style={{ color: '#2E8255', flex: 'none', marginTop: 1 }} />
            <p style={{ fontSize: 12.5, color: 'var(--ink-soft)', margin: 0 }}>
              All information has been automatically filled. Scroll down and click Submit.
            </p>
          </div>

          <iframe
            src={xactusIframeUrl}
            tabIndex={0}
            style={{ width: '100%', height: 600, border: '1px solid var(--line)', borderRadius: 9 }}
            title="Xactus Credentials Request Form"
          />
        </LwModal>
      )}
    </div>
  );
};
